import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewChild } from "@angular/core";
import { EditMode, EventType, RobotRulesService } from "../robot-rules.service";
import _, { includes, isNil, range } from "lodash";
import { NzMessageService } from "ng-zorro-antd/message";
import { NzModalRef, NzModalService } from "ng-zorro-antd/modal";
import { TestRuleComponent } from "@root/router/component/automation/robot-rules/test-rule/test-rule.component";
import { TranslateService } from "@root/shared/service/translate.service";
import * as moment from "moment";
import { PriorityRuleComponent } from "@root/router/robot/view/priority-rule/priority-rule.component";
import { AppService } from "@root/app.service";
import { HttpClient } from "@angular/common/http";
import { padStart } from "@root/util/date";
import { DateService } from "@root/shared/service/date.service";
import { Observable, of } from "rxjs";

type LimitValue = "nights" | "no_left_review" | "children" | "infants" | "pets" | "booked_last_min";

@Component({
  selector: "host-message-rules",
  templateUrl: "./message-rules.component.html",
  styleUrls: ["./message-rules.component.scss"],
})
export class MessageRulesComponent implements OnInit {
  private readonly GroupListApi = "/api/house_group/group_list";
  private readonly SubmitV2Api = "/api/automation/message/v2_rule_save";
  private readonly DetailV2Api = "/api/automation/message/v2_rule_detail";
  private readonly UploadApi = "/api/upload/image";

  @ViewChild("textarea", { static: true }) textarea;
  readonly EventType: typeof EventType = EventType;
  private readonly EditMode: typeof EditMode = EditMode;
  private readonly EventMap: Record<EventType, Array<any>> = {
    [EventType.ReplyToEvents]: [
      {
        label: this.t.t("robot.rule.book_event.new_res_request"),
        value: "new_reservation_request",
        type: 1,
      },
      {
        label: this.t.t("robot.rule.book_event.new_order"),
        value: "new_reservation_paid",
        type: 1,
      },
      {
        label: this.t.t("robot.rule.book_event.res_canceled"),
        value: "new_reservation_canceled",
        type: 1,
      },
    ],
    [EventType.SendOnSchedule]: [
      {
        label: this.t.t("robot_rules-message_rules_ts-订单生成后"),
        value: "after_new_reservation",
        type: 2,
      },
      {
        label: this.t.t("robot_rules-message_rules_ts-431_入住前"),
        value: "before_checkin",
        type: 0,
      },
      {
        label: this.t.t("robot_rules-message_rules_ts-431_入住日"),
        value: "on_checkin_day",
        type: 0,
      },
      {
        label: this.t.t("robot_rules-message_rules_ts-431_入住后"),
        value: "after_checkin",
        type: 1,
      },
      {
        label: this.t.t("robot_rules-message_rules_ts-431_退房前"),
        value: "before_checkout",
        type: 0,
      },
      {
        label: this.t.t("robot_rules-message_rules_ts-431_退房日"),
        value: "on_checkout_day",
        type: 0,
      },
      {
        label: this.t.t("robot_rules-message_rules_ts-431_退房后"),
        value: "after_checkout",
        type: 1,
      },
    ],
    [EventType.Inquiry]: [
      {
        label: this.t.t("automation.message_rule.inquiry.inquiry"),
        value: "inquiry",
        type: 1,
      },
    ],
    [EventType.Keyword]: [
      {
        label: this.t.t("automation.message_rule.keyword.all"),
        value: "keyword_all",
        type: 1,
      },
      {
        label: this.t.t("automation.message_rule.keyword.any"),
        value: "keyword_any",
        type: 1,
      },
    ],
    [EventType.Underwriting]: [
      {
        label: this.t.t("automation.message_rule.underwriting.any"),
        value: "underwriting_any",
        type: 1,
      },
      {
        label: this.t.t("automation.message_rule.underwriting.first"),
        value: "underwriting_first_message",
        type: 1,
      },
    ],
  };
  @Input() editMode: EditMode = EditMode.Create;
  @Input() type: EventType = EventType.ReplyToEvents;
  /** 初始值，只有编辑模式下和复制模式下才有 */
  @Input() id;
  @Input() event = null;
  @Input() listOfChannelOption = [];
  @Input() listOfHouseOption = [];
  @Input() listOfDynContentOption = [];
  listOfChannelAccountOption = [];
  listOfStageOption = [
    {
      label: "预订前",
      value: "before_reservation",
      key: "robot.rule.condition.opt_before_book",
    },
    {
      label: "入住前",
      value: "before_checkin",
      key: "robot.rule.condition.opt_before_checkin",
    },
    {
      label: "入住中",
      value: "staying_in",
      key: "robot.rule.condition.opt_staying",
    },
  ].map((opt) => ({
    label: this.t.t(opt.key),
    value: opt.value,
    checked: this.editMode === EditMode.Create ? true : false,
  }));
  listOfDayOptions = [];
  listOfTimeOptions = range(0, 24).map((i) => ({
    label: `${padStart(i)}:00`,
    value: `${padStart(i)}:00`,
  }));
  // listOfTimeOptions = [[], []];
  listOfGroupOptions: Option[] = [];

  listOfAfterResvOption = [0, 0.5, ...range(1, 13), 24, 36, 48, 60, 72].map((i) => ({
    label:
      i === 0
        ? this.t.t("automatiion-message_rules_html_immediately")
        : this.t.select(
            ["automation.message_rule.option.hour.single", "automation.message_rule.option.hour.plural"],
            "number",
            i,
          ),
    value: i,
  }));

  listOfDelayTimeOptions: [Option[], Option[]] = [
    this.generateTimeOptions(100, "hour"),
    this.generateTimeOptions(60, "minute"),
  ];

  listOfLimitOption: Array<{ label: string; value: LimitValue }> = [
    { label: "Length of stay", value: "nights" },
    { label: "Guests who have not yet left a review", value: "no_left_review" },
    { label: "Guests including children", value: "children" },
    { label: "Guests including infants", value: "infants" },
    { label: "Guests with pets", value: "pets" },
    { label: "Guests who booked last minute", value: "booked_last_min" },
  ];

  limitValues: Set<LimitValue> = new Set([]);

  detail; // 详情，只有 edit 模式下才有

  actionQuickMsgId = null; // 快捷消息id，textType = 1 时有效

  //#region 4.4.0 =============================================
  //
  // 通用属性
  //
  channel = null;
  account = null;
  properties = {
    /**
     * 后端接受的值为 all | by_property | by_group | by_room_type
     * 前端因为兼容问题，组件接受的值为 none | house | room_type | group
     * 所以在 save 和获取 detail 的时候，需要转换一下
     */
    type: "none",
    value: null,
  };
  content;
  contentImage;

  //
  // ReplyToEvents 和 ReplyToSpecificQuestions 专有
  //
  /**
   * 后端接受的值为 number，即分钟数
   * 前端组件接受的值为 number[]，分别代表时，分
   * save 和获取 detail 的时候需要转换一下
   */
  delayTime: [number, number] = [0, 0];

  // SendOnSchedule 专有
  at = "00:00";
  days = 0;
  specific = {
    frequency: "the_first",
    day_of_week: 1,
    condition: "including",
  };
  nights = {
    start: 1,
    end: -1,
  };

  // nightsStart = 1;
  // nightsEnd = -1;

  name; // rule name

  limitSettings = false;

  afterHours = 0; // after new reservation专有

  //
  // ReplyToSpecificQuestions 专有
  //
  /**
   * stage 是一个 checkbox 组，选中状态由列表控制，这里仅仅用于便捷获取选中的值
   */
  get stage() {
    return this.listOfStageOption?.filter((s) => s.checked).map((s) => s.value);
  }

  period = {
    start: null,
    end: null,
  };

  /**
   * 下面两个字段只有在 event === EventType.Keyword 的时候才有
   */
  keyword: null;
  keywordCondition = "keyword_all"; // 'keyword_all' || 'keyword_any'

  /**
   * 这个字段只有在 event === EventType.Underwriting 的时候才有
   */
  fallbackCondition = "underwriting_any";

  /**
   * 这个字段只有在 event === EventType.Inquiry 的时候才有
   */
  inquiryId: null;

  limit: {
    nights_start?: number;
    nights_end?: number;
    no_review?: number;
    with_children?: number;
    with_infants?: number;
    with_pets?: number;
    booked_before_day?: number;
  };
  //#endregion

  use12Hr = this.dateSrv.use12hr;

  get eventOptions() {
    return this.EventMap[this.type];
  }

  get nodeTypeName() {
    if (this.type === EventType.SendOnSchedule) {
      const node = this.eventOptions.find((opt) => opt.value === this.event);
      return node && node.label;
    }
    return this.t.t("robot.rule.word.delay");
  }

  get displayNights() {
    return this.type === EventType.ReplyToEvents || this.type === EventType.SendOnSchedule;
  }

  get displaySettings() {
    return this.event === "after_checkout" || this.event === "on_checkout_day";
  }

  get displayCheckDaySelect() {
    return this.event !== "on_checkin_day" && this.event !== "on_checkout_day";
  }

  get isInternational() {
    return this.app.app.international;
  }

  constructor(
    private dateSrv: DateService,
    private robotSrv: RobotRulesService,
    private http: HttpClient,
    private messageService: NzMessageService,
    private modal: NzModalService,
    private modalRef: NzModalRef,
    private t: TranslateService,
    private app: AppService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    if (this.editMode === EditMode.Update || this.editMode === EditMode.Copy) {
      this.fetchDetailV2();
    } else {
      this.initSelectOptions();
      this.days = this.selectMinDays();

      if (this.type == EventType.Inquiry) {
        /**
         * 之前的逻辑里，inquiryId 使用的是 event 字段，因此这里兼容一下
         */
        this.inquiryId = this.event;
        this.event = "inquiry";
      }
    }
  }

  private initDayOptions() {
    this.listOfDayOptions = range(this.selectMinDays(), this.selectMaxDays()).map((i) => ({
      label: this.t.t(`automation.message_rule.option.day.${i < 2 ? "single" : "plural"}`, { number: i }),
      value: i,
    }));
  }

  private initSelectOptions() {
    this.fetchChannelAccounts();
    this.initGroupOptions();
    this.initDayOptions();
    if (this.type === EventType.Inquiry) {
      this.fetchQuestionList();
    }

    // if (this.event === EventType.SendOnSchedule) {
    //   this.listOfDelayTimeOptions = [this.generateTimeOptions(10, 'day'), this.generateTimeOptions(24, 'hour')];
    // }
  }

  private initGroupOptions() {
    this.http.get(this.GroupListApi).subscribe((rst: any) => {
      this.listOfGroupOptions = rst?.data?.list?.map((item) => ({
        label: item.name,
        value: item.group_id,
      }));
      this.listOfGroupOptions.unshift({
        label: "Any",
        value: -1,
      });
    });
  }

  private fetchQuestionList() {
    this.robotSrv.fetchAllQuestion().subscribe((res) => {
      const list = _.get(res, "data.list") || [];
      this.EventMap[EventType.Inquiry] = list.map((question) => ({
        label: this.isInternational ? question.description : question.description_zh,
        value: question.id,
        type: 1,
      }));
    });
  }

  private fetchChannelAccounts() {
    const params = {
      from: "message",
    };
    if (this.channel) {
      Object.assign(params, { thirdparty_types: JSON.stringify(this.channel) });
    }
    // const channels = this.scopeChannels.filter((c) => c !== -1);
    this.robotSrv.fetchChannelAccounts(params).subscribe((res) => {
      const list = _.get(res, "data.list");
      this.listOfChannelAccountOption = list.map((account) => ({
        ...account,
        label: account.username,
        value: account.id,
      }));
    });
  }

  private selectMinDays() {
    if (includes(["before_checkin", "after_checkin", "before_checkout", "after_checkout"], this.event)) {
      return 1;
    }
    return 0;
  }

  private selectMaxDays() {
    if (includes(["before_checkin", "after_checkin", "before_checkout", "after_checkout"], this.event)) {
      return 61;
    }
    return 10;
  }

  private generateTimeOptions(length, unit: "day" | "hour" | "minute") {
    return Array(length)
      .fill(0)
      .map((v, i) => ({
        label: this.t.t(`automation.message_rule.option.${unit}.${i < 2 ? "single" : "plural"}`, {
          number: i,
        }),
        value: i,
      }));
  }

  onToggleLimitValue(value: LimitValue) {
    if (this.limitValues.has(value)) {
      this.limitValues.delete(value);
    } else {
      this.limitValues.add(value);
    }
  }

  onClickTestRule() {
    this.modal.create({
      nzTitle: this.t.t("robot.rule.modal.test.title"),
      nzContent: TestRuleComponent,
      nzFooter: null,
      nzWidth: 520,
    });
  }

  onClickPriorityRule() {
    this.modal.create({
      // nzTitle: '规则的优先级',
      nzTitle: this.t.t("automation.message_rule.modal.priority_title"),
      nzContent: PriorityRuleComponent,
      nzWidth: 400,
      nzFooter: null,
    });
  }

  // onClickEditQuickMsg() {
  //   this.modalService.create({
  //     nzTitle: this.t.t("dialogue_setting.modify_quick_message"),
  //     nzContent: EditTemplateComponent,
  //     nzWidth: 480,
  //     nzFooter: null,
  //     nzComponentParams: {
  //       id: this.actionQuickMsgId,
  //       // isSubAccount: this.isSubAccount,
  //       editMode: EM.Update,
  //     },
  //   });
  // }

  onSelectChannels(channels = []) {
    this.fetchChannelAccounts();
  }

  onSelectQuickMsgOfAction(data = { id: null, content: "" }) {
    const { id, content } = data;
    this.content = (this.content || "") + content;
  }

  onSelectDynContent(content) {
    // const value = this.actionTextContent;
    const value = this.content || "";
    const pos = this.textarea.nativeElement.selectionStart;

    this.content = value.slice(0, pos) + `{{${content}}}` + value.slice(pos);
  }

  onSelectStage(data) {
    const checked = data.some((opt) => opt.checked);
    if (!checked) {
      // this.messageService.warning('请至少选择一个阶段');
      this.messageService.warning(this.t.syncGet("automation.message_rule.toast.select_stage"));
    }
  }

  private propertyTypeConverter(type) {
    const map = {
      none: "all",
      house: "by_property",
      room_type: "by_room_type",
      group: "by_group",
      all: "none",
      by_property: "house",
      by_room_type: "room_type",
      by_group: "group",
    };
    return map[type];
  }

  private periodTimeConverter(date: Date): string {
    return moment(date).format("HH:mm");
  }

  /**
   * 4.4.0中 type 由原来的 5 个值缩为 3 个值，keyword,underwriting 和 inquiry 变为了 reply_to_specific_questions
   * 但是前端依然需要区分这三个具体的类型，以便进行不同的 ui 展示，所以这里根据 subevent 做一个映射
   *
   */
  private compatTypeConverterForKeyQuestions(event: string) {
    if (event.startsWith("keyword_")) {
      return EventType.Keyword;
    } else if (event.startsWith("underwriting_")) {
      return EventType.Underwriting;
    }
    return EventType.Inquiry;
  }

  /**
   * 兼容性的处理
   *
   * 理论上 event 代指事件，具体的值由其他字段处理。
   * 比如 inquiryId 代表选择的问题，keywordCondition 代表选择的 keyword 条件，fallbackCondition 代表选择的 fallback 条件。
   *
   * 但是接口设计失误，keyword 和 fallback 事件里，event 承载了具体的值，而没有和 inquiry 一样使用独立的字段，所以这里做一个兼容性处理，用于返回这两个字段
   */
  private compatEventConverterForQuestions(type: EventType) {
    if (type === EventType.Keyword) {
      return this.keywordCondition;
    } else if (type === EventType.Underwriting) {
      return this.fallbackCondition;
    }

    // return this.event;
    return "inquiry";
  }

  private collectParamsV2() {
    switch (this.type) {
      case EventType.ReplyToEvents:
        return this.collectReplyToEventsParams();
      case EventType.SendOnSchedule:
        return this.collectSendOnScheduleParams();
      default:
        return this.collectQuestionsParams();
    }
  }

  private collectReplyToEventsParams() {
    return {
      name: this.name,
      type: EventType.ReplyToEvents,
      event: this.event,
      channel: this.channel,
      account: this.account,
      property_type: this.propertyTypeConverter(this.properties.type),
      property_ids: this.properties.value,
      content: this.content,
      content_image_url: this.contentImage,
      delay_minute: this.delayTime[0] * 60 + this.delayTime[1],

      limit_nights_start: this.limit?.nights_start,
      limit_nights_end: this.limit?.nights_end,
      limit_with_children: this.limit?.with_children,
      limit_with_infants: this.limit?.with_infants,
      limit_with_pets: this.limit?.with_pets,
      limit_booked_before_day: this.limit?.booked_before_day,
    };
  }

  private collectSendOnScheduleParams() {
    return {
      name: this.name,
      type: EventType.SendOnSchedule,
      event: this.event,
      channel: this.channel,
      account: this.account,
      property_type: this.propertyTypeConverter(this.properties.type),
      property_ids: this.properties.value,
      content: this.content,
      content_image_url: this.contentImage,
      at: this.at,
      days: this.days,
      frequency: this.specific.frequency,
      dayofweek: this.specific.day_of_week,
      checkin_day_include: this.specific.condition,
      nights_start: this.nights.start,
      nights_end: this.nights.end,
      settings_only_not_review: +this.limitSettings,
      after_hours: this.afterHours,

      limit_nights_start: this.limit?.nights_start,
      limit_nights_end: this.limit?.nights_end,
      limit_with_children: this.limit?.with_children,
      limit_with_infants: this.limit?.with_infants,
      limit_with_pets: this.limit?.with_pets,
      limit_not_review: this.limit?.no_review,
    };
  }

  private collectQuestionsParams() {
    console.log("this.event: ", this.event);
    return {
      name: this.name,
      type: "reply_to_specific_questions",
      event: this.compatEventConverterForQuestions(this.event),
      channel: this.channel,
      account: this.account,
      property_type: this.propertyTypeConverter(this.properties.type),
      property_ids: this.properties.value,
      content: this.content,
      content_image_url: this.contentImage,
      delay_minute: this.delayTime[0] * 60 + this.delayTime[1],
      stage: this.stage,
      period_start: this.period?.start ? this.periodTimeConverter(this.period.start) : null,
      period_end: this.period?.end ? this.periodTimeConverter(this.period.end) : null,
      keyword: this.keyword,
      inquiry_id: this.inquiryId,
    };
  }

  private parseFromLimit(data) {
    const limit = {};
    if (!isNil(data.limit_nights_start)) {
      limit["nights_start"] = data.limit_nights_start;
      limit["nights_end"] = data.limit_nights_end;
    }
    if (!!data.limit_with_children) {
      limit["with_children"] = 1;
    }
    if (!!data.limit_with_infants) {
      limit["with_infants"] = 1;
    }
    if (!!data.limit_with_pets) {
      limit["with_pets"] = 1;
    }

    if (!!data.limit_not_review) {
      limit["no_review"] = 1;
    }

    if (!isNil(data.limit_booked_before_day)) {
      limit["booked_before_day"] = data.limit_booked_before_day;
    }

    return limit;
  }

  private parseFromReplyToEventsData(data) {
    this.name = data.name;
    this.type = data.type;
    this.event = data.event;
    this.channel = data.channel;
    this.account = data.account;
    this.properties = {
      type: this.propertyTypeConverter(data.property_type),
      value: data.property_ids,
    };
    this.content = data.content;
    this.contentImage = data.content_image_url;
    this.delayTime = data.delay_minute ? [Math.floor(data.delay_minute / 60), data.delay_minute % 60] : [0, 0];

    this.limit = this.parseFromLimit(data);

    console.log("limit: ", this.limit);
  }

  private parseFromSendOnScheduleData(data) {
    this.name = data.name;
    this.type = data.type;
    this.event = data.event;
    this.channel = data.channel;
    this.account = data.account;
    this.properties = {
      type: this.propertyTypeConverter(data.property_type),
      value: data.property_ids,
    };
    this.content = data.content;
    this.contentImage = data.content_image_url;
    this.days = data.days;
    this.at = data.at;

    this.nights = {
      start: data.nights_start || 1,
      end: data.nights_end || -1,
    };

    // this.limitSettings = !!data.settings_only_not_review;

    this.specific = {
      frequency: data.frequency || "the_first",
      day_of_week: data.dayofweek || 1,
      condition: data.checkin_day_include || "including",
    };

    this.afterHours = data.after_hours || 0;

    this.limit = this.parseFromLimit(data);
  }

  private parseFromQuestionsData(data) {
    this.name = data.name;
    this.type = this.compatTypeConverterForKeyQuestions(data.event);
    this.event = this.compatTypeConverterForKeyQuestions(data.event);
    this.channel = data.channel;
    this.account = data.account;
    this.properties = {
      type: this.propertyTypeConverter(data.property_type),
      value: data.property_ids,
    };
    this.content = data.content;
    this.contentImage = data.content_image_url;
    this.delayTime = data.delay_minute ? [Math.floor(data.delay_minute / 60), data.delay_minute % 60] : [0, 0];
    this.listOfStageOption = this.listOfStageOption.map((opt) => ({
      ...opt,
      checked: includes(data.stage, opt.value),
    }));
    this.keyword = data.keyword;
    this.inquiryId = data.inquiry_id;
    this.period = {
      start: data.period_start ? moment(data.period_start, "HH:mm").toDate() : null,
      end: data.period_end ? moment(data.period_end, "HH:mm").toDate() : null,
    };

    if (this.type === EventType.Keyword) {
      this.keywordCondition = data.event;
      // this.event = EventType.Keyword
    } else if (this.type === EventType.Underwriting) {
      this.fallbackCondition = data.event;
      // this.event = EventType.Underwriting;
    }
  }

  private requestSubmitV2(extend = false) {
    const params = this.collectParamsV2();

    Object.assign(params, {
      extend_exist_reservation: extend ? 1 : 0,
    });

    if (this.editMode === EditMode.Update) {
      /** 新建和复制都不传id */
      Object.assign(params, { id: this.id });
    }

    return this.http.post(this.SubmitV2Api, params).subscribe((rst: any) => {
      if (rst.error_code === 0) {
        this.messageService.success(this.t.t("word.success"));
        this.close();
      }
    });
  }

  private fetchDetailV2() {
    const params = { id: this.id };
    return this.http.get(this.DetailV2Api, { params }).subscribe((rst: any) => {
      const data = rst.data?.record;
      this.detail = data;
      switch (data.type) {
        case EventType.ReplyToEvents:
          this.parseFromReplyToEventsData(data);
          break;
        case EventType.SendOnSchedule:
          this.parseFromSendOnScheduleData(data);
          break;
        default:
          this.parseFromQuestionsData(data);
      }

      this.initSelectOptions();
    });
  }

  private requestUpload(file) {
    const data = new FormData();
    data.append("file", file);
    this.http.post(this.UploadApi, data).subscribe((rst: any) => {
      this.contentImage = rst?.data?.file;
    });
  }

  private close() {
    this.modalRef.close();
  }

  //#region Apply Modification
  private applyModifyWarning(): Observable<"ok" | "cancel"> {
    return new Observable((observer) => {
      const modal = this.modal.confirm({
        nzTitle: this.t.t("auto-apply-to-resvs_modal_title"),
        nzContent: this.t.t("auto-apply-to-resvs_modal_content"),
        nzWidth: 540,
        nzCancelText: this.t.t("word.no"),
        nzOkText: this.t.t("word.yes"),
        nzOnOk: () => {
          observer.next("ok");
        },
        nzOnCancel: () => {
          observer.next("cancel");
        },
      });

      modal.afterClose.subscribe(() => observer.complete());
    });
  }

  // private requestApplyToResvs() {
  //   const params = {};
  //   if (this.editMode === EditMode.Update) {
  //     Object.assign(params, { id: this.id });
  //   }

  //   this.robotSrv.requestApplyToResvsForMessage(params).subscribe();
  // }

  //#endregion

  onUploadFiles(files: File[]) {
    this.requestUpload(files?.[0]);
  }

  onSave() {
    let warning$ = of<"ok" | "cancel">("cancel");
    if (this.editMode === EditMode.Update && this.content !== this.detail.content) {
      warning$ = this.applyModifyWarning();
    }
    warning$.subscribe((status) => {
      this.requestSubmitV2(status === "ok");
    });
  }

  onCancel() {
    this.close();
  }
}
